/**
 * WHAT A TERMINAL'S STATUS IS — told from the till, not the paperwork.
 *
 * A terminal has two records. PROVISION# holds the code and a `status` that
 * only the Activate and Deregister buttons ever write. REGISTER# is the till
 * itself. The pages printed the PROVISION status, so a till that missed its
 * `terminal:revoke` push — connected and mid-shift — read "Pending" forever
 * while the live WebSocket said it was online.
 *
 * The till's live connection and its register row outrank the provision row.
 * Keep in step with `terminalDisplayStatus.ts` in platform-app.
 */
export type DisplayTone = 'emerald' | 'amber' | 'red' | 'stone';

export interface TerminalDisplayStatus {
  label: string;
  tone: DisplayTone;
  /** A register exists or the till is connected — online/offline applies. */
  registered: boolean;
  /** Why the status reads as it does, when that is not obvious. */
  hint?: string;
}

const REGISTER_LABEL: Record<string, { label: string; tone: DisplayTone }> = {
  // in_session = the cash-drawer shift is open (signing out does not close it).
  in_session: { label: 'Shift open', tone: 'emerald' },
  active: { label: 'Active', tone: 'emerald' },
  maintenance: { label: 'Maintenance', tone: 'amber' },
  inactive: { label: 'Inactive', tone: 'stone' },
};

export function terminalDisplayStatus(
  t: { terminalType?: string; status?: string; registerStatus?: string | null; expiresAt?: number | null },
  isOnline: boolean,
): TerminalDisplayStatus {
  if (t.terminalType === 'online_sales') return { label: 'Active', tone: 'emerald', registered: true };

  const registered = isOnline || !!t.registerStatus || t.status === 'activated';
  if (!registered) {
    const expired = t.status === 'pending' && !!t.expiresAt && Date.now() > t.expiresAt;
    return expired
      ? { label: 'Expired', tone: 'red', registered: false }
      : { label: 'Pending', tone: 'amber', registered: false };
  }

  if (t.status === 'pending' && !isOnline) {
    return {
      label: 'Deregistered',
      tone: 'amber',
      registered: true,
      hint: 'Deregistered and not connected. Re-enter the provisioning code on the device to bring it back.',
    };
  }

  const reg = (t.registerStatus && REGISTER_LABEL[t.registerStatus]) || { label: 'Activated', tone: 'emerald' as DisplayTone };
  return {
    ...reg,
    registered: true,
    ...(t.status === 'pending'
      ? { hint: 'This till was deregistered but never received the revoke — it is still connected and trading.' }
      : {}),
  };
}

/** Badge classes for the black page header. */
export const DARK_BADGE: Record<DisplayTone, string> = {
  emerald: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  red: 'bg-red-500/20 text-red-400 border-red-500/30',
  stone: 'bg-stone-500/20 text-stone-300 border-stone-500/30',
};

/** Badge classes for white cards. */
export const LIGHT_BADGE: Record<DisplayTone, string> = {
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  red: 'bg-red-50 text-red-700 border-red-200',
  stone: 'bg-stone-100 text-stone-600 border-stone-200',
};
